import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashboardService } from "../../services/dashboard.service";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

export const PatientAccountReceivableWidget: React.FC = () => {
  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["accounts-receivable-patients"],
    queryFn: () => dashboardService.getAccountsReceivablePatients(),
  });

  const chartData = useMemo(() => {
    if (!metrics || metrics.length === 0) return [];

    const bucketsMap: Record<string, number> = {};
    let totalValue: number | null = null;

    metrics.forEach((metric) => {
      const key = metric.metricName;
      const value = parseFloat(metric.value?.toString() || "0");
      if (!isNaN(value)) {
        if (key === "total") {
          totalValue = value;
        } else {
          bucketsMap[key] = value;
        }
      }
    });

    const sortedBucketNames = Object.keys(bucketsMap).sort((a, b) => {
      if (a.startsWith(">")) return 1;
      if (b.startsWith(">")) return -1;
      return parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]);
    });

    const totalSum =
      totalValue !== null
        ? totalValue
        : sortedBucketNames.reduce((sum, key) => sum + bucketsMap[key], 0);

    const result = sortedBucketNames.map((key) => {
      const value = bucketsMap[key];
      return {
        name: key.startsWith(">") ? key : key.split("-")[0],
        fullLabel: `${key} days`,
        value,
        percentage: totalSum > 0 ? (value / totalSum) * 100 : 0,
        isTotal: false,
      };
    });

    if (totalValue !== null) {
      result.push({
        name: "Totals",
        fullLabel: "Totals",
        value: totalValue,
        percentage: 100,
        isTotal: true,
      });
    }

    return result;
  }, [metrics]);

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return "$0";
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const chartBuckets = chartData.filter((d) => !d.isTotal);

  const series = [{ name: "Amount", data: chartBuckets.map((d) => d.value) }];

  const options: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false }, animations: { enabled: true } },
    grid: { strokeDashArray: 3 },
    colors: ["#6B9FBD"],
    plotOptions: { bar: { columnWidth: "45%", borderRadius: 2 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: chartBuckets.map((d) => d.fullLabel),
      labels: {
        show: true,
        rotate: -45,
        rotateAlways: true,
        style: { fontSize: "10px", colors: "#6b7280" },
        offsetY: 5,
      },
      axisBorder: { show: true, color: "#BCBDC5" },
      axisTicks: { show: true, color: "#BCBDC5" },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => formatCurrency(val),
        style: { fontSize: "12px" },
      },
    },
    tooltip: {
      intersect: false,
      followCursor: false,
      y: {
        formatter: (val: number, opts?: any) => {
          const idx = opts?.dataPointIndex ?? 0;
          const dataPoint = chartBuckets[idx];
          const pct = dataPoint?.percentage ? ` (${dataPoint.percentage.toFixed(1)}%)` : "";
          return `${formatCurrency(val)}${pct}`;
        },
        title: {
          formatter: (_: string, opts?: any) => {
            const idx = opts?.dataPointIndex ?? 0;
            return chartBuckets[idx]?.fullLabel || "";
          },
        },
      },
    },
  };

  return (
    <div className="bg-white border-2 border-border-gray flex flex-col">
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Patient A/R</h3>
        <span className="text-xs sm:text-sm">Aging Analysis</span>
      </div>

      <div className="flex flex-col">
        {isLoading && (
          <div className="text-center p-4 sm:p-6 py-8 text-gray-500">
            Loading accounts receivable data...
          </div>
        )}

        {error && (
          <div className="text-center p-4 sm:p-6 py-8 text-red-600">
            Failed to load accounts receivable data. Please try again.
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="w-full">
              <ReactApexChart options={options} series={series} type="bar" height={200} />
            </div>

            <div className="max-h-[180px] overflow-y-auto border-t-2 border-border-gray">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                      Date Range
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-gray">
                  {chartData.map((item, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50${item.isTotal ? " font-semibold bg-gray-50" : ""}`}
                    >
                      <td className="px-4 py-2 text-sm font-medium">{item.fullLabel}</td>
                      <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.value)}</td>
                      <td className="px-4 py-2 text-sm text-right">
                        {item.isTotal ? "100%" : `${item.percentage.toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
